import { useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { usePostHog } from 'posthog-js/react'
import { login, logout } from '../../store/user'
import { useUserInfo } from '../../components/Authentication/hooks'

/**
 * Auth event type constants for better type safety and consistency
 */
export const AUTH_EVENT_TYPES = {
  SIGN_IN: 'signIn',
  SIGNED_IN: 'signedIn',
  SIGN_UP: 'signUp',
  SIGN_OUT: 'signOut',
  SIGN_IN_FAILURE: 'signIn_failure',
  TOKEN_REFRESH: 'tokenRefresh',
  CONFIGURED: 'configured',
  CUSTOM_OAUTH_STATE: 'customOAuthState',
  PARSING_CALLBACK_URL: 'parsingCallbackUrl'
}

/**
 * Auth event groups for filtering
 */
export const AUTH_EVENT_GROUPS = {
  SIGN_IN_EVENTS: [AUTH_EVENT_TYPES.SIGN_IN, AUTH_EVENT_TYPES.SIGNED_IN],
  SIGN_UP_EVENTS: [AUTH_EVENT_TYPES.SIGN_UP],
  SIGN_OUT_EVENTS: [AUTH_EVENT_TYPES.SIGN_OUT],
  ERROR_EVENTS: [AUTH_EVENT_TYPES.SIGN_IN_FAILURE],
  TOKEN_EVENTS: [AUTH_EVENT_TYPES.TOKEN_REFRESH],
  OAUTH_EVENTS: [
    AUTH_EVENT_TYPES.CUSTOM_OAUTH_STATE,
    AUTH_EVENT_TYPES.PARSING_CALLBACK_URL
  ],
  CONFIG_EVENTS: [AUTH_EVENT_TYPES.CONFIGURED],
  ALL_EVENTS: Object.values(AUTH_EVENT_TYPES)
}

/**
 * Default event filtering configuration
 */
export const DEFAULT_EVENT_FILTER = {
  includeEvents: AUTH_EVENT_GROUPS.ALL_EVENTS,
  excludeEvents: [],
  enableLogging: true,
  enableErrorHandling: true
}

/**
 * Check if an event should be processed based on filter configuration
 * @param {string} event - Auth event type
 * @param {Object} filterConfig - Event filtering configuration
 * @returns {boolean} True if event should be processed
 */
export const shouldProcessEvent = (
  event,
  filterConfig = DEFAULT_EVENT_FILTER
) => {
  const { includeEvents, excludeEvents } = filterConfig

  // Check if event is excluded
  if (excludeEvents.includes(event)) {
    return false
  }

  // Check if event is included
  if (includeEvents.length > 0 && !includeEvents.includes(event)) {
    return false
  }

  return true
}

/**
 * Create event filter for specific event groups
 * @param {Array} eventGroups - Array of event group names
 * @returns {Object} Event filter configuration
 */
export const createEventGroupFilter = eventGroups => {
  const includeEvents = eventGroups.reduce((acc, groupName) => {
    const events = AUTH_EVENT_GROUPS[groupName] || []
    return [...acc, ...events]
  }, [])

  return {
    ...DEFAULT_EVENT_FILTER,
    includeEvents
  }
}

/**
 * Log auth event with error handling
 * @param {string} event - Auth event type
 * @param {Object} data - Event data
 * @param {Object} filterConfig - Event filtering configuration
 */
export const logAuthEvent = (
  event,
  data,
  filterConfig = DEFAULT_EVENT_FILTER
) => {
  if (!filterConfig.enableLogging) return

  try {
    if (AUTH_EVENT_GROUPS.ERROR_EVENTS.includes(event)) {
      console.error(`Auth ${event} event:`, data)
    } else {
      console.log(`Auth ${event} event:`, data)
    }
  } catch (error) {
    console.error('Error logging auth event:', error)
  }
}

/**
 * Handle auth event with comprehensive error handling
 * @param {string} event - Auth event type
 * @param {Object} data - Event data
 * @param {Object} handlers - Event handler functions
 * @param {Object} filterConfig - Event filtering configuration
 */
export const handleAuthEventSafely = (
  event,
  data,
  handlers,
  filterConfig = DEFAULT_EVENT_FILTER
) => {
  try {
    if (!shouldProcessEvent(event, filterConfig)) {
      return
    }

    logAuthEvent(event, data, filterConfig)

    switch (event) {
      case AUTH_EVENT_TYPES.SIGN_IN:
        if (handlers.onSignIn) {
          handlers.onSignIn(data)
        }
        break
      case AUTH_EVENT_TYPES.SIGNED_IN:
        if (handlers.onSignedIn) {
          handlers.onSignedIn(data)
        }
        break
      case AUTH_EVENT_TYPES.SIGN_UP:
        if (handlers.onSignUp) {
          handlers.onSignUp(data)
        }
        break
      case AUTH_EVENT_TYPES.SIGN_OUT:
        if (handlers.onSignOut) {
          handlers.onSignOut(data)
        }
        break
      case AUTH_EVENT_TYPES.SIGN_IN_FAILURE:
        if (handlers.onSignInFailure) {
          handlers.onSignInFailure(data)
        }
        break
      case AUTH_EVENT_TYPES.TOKEN_REFRESH:
        if (handlers.onTokenRefresh) {
          handlers.onTokenRefresh(data)
        }
        break
      case AUTH_EVENT_TYPES.CONFIGURED:
      case AUTH_EVENT_TYPES.CUSTOM_OAUTH_STATE:
      case AUTH_EVENT_TYPES.PARSING_CALLBACK_URL:
        // Silent events - no action needed but included for completeness
        break
      default:
        if (handlers.onUnhandledEvent) {
          handlers.onUnhandledEvent(event, data)
        } else {
          console.warn('Unhandled auth event:', event, ', data:', data)
        }
        break
    }
  } catch (error) {
    if (filterConfig.enableErrorHandling) {
      console.error(`Error handling auth event ${event}:`, error)
      if (handlers.onError) {
        handlers.onError(error, event, data)
      }
    } else {
      throw error
    }
  }
}

// Default empty handlers object, to be used instead of {} to avoid re-renders.
const EMPTY_HANDLERS = {}

/**
 * Custom hook for listening to authentication events with enhanced error handling and filtering
 * @param {Object} options - Configuration options
 * @param {Object} options.eventFilter - Event filtering configuration
 * @param {Function} options.onError - Custom error handler
 * @param {Object} options.customHandlers - Custom event handlers to override defaults
 */
export const useAuthListener = (options = {}) => {
  const {
    eventFilter = DEFAULT_EVENT_FILTER,
    onError,
    customHandlers = EMPTY_HANDLERS
  } = options

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const posthog = usePostHog()
  const { userId, userEmail, user, isSignedIn } = useUserInfo()

  // Default event handlers memoized to prevent unnecessary re-renders
  const defaultHandlers = useMemo(
    () => ({
      onSignIn: data => {
        const { userId, userEmail, firstName, lastName, imageUrl, createdAt } = data || {}
        if (userId && userEmail) {
          dispatch(login({ userId, userEmail, firstName, lastName, imageUrl, createdAt }))
        }
      },

      onSignedIn: data => {
        // Silent success event
      },

      onSignUp: data => {
        try {
          const { userId, userEmail, firstName, lastName, imageUrl, createdAt } = data || {}
          if (userId && posthog) {
            // Identify user first so the sign_up event is attributed to them
            posthog.identify(userId, {
              email: userEmail,
              first_name: firstName,
              last_name: lastName,
              name: [firstName, lastName].filter(Boolean).join(' ') || undefined,
              avatar: imageUrl,
              created_at: createdAt
            })
            posthog.capture('sign_up', { username: userId })
          }
        } catch (error) {
          console.error('Error tracking sign up event:', error)
        }
      },

      onSignOut: data => {
        try {
          dispatch(logout())
          navigate('/auth')
        } catch (error) {
          console.error('Error handling sign out:', error)
        }
      },

      onSignInFailure: data => {
        console.error('Sign in failure:', data)
      },

      onTokenRefresh: data => {
        console.log('Token refreshed successfully')
      },

      onUnhandledEvent: (event, data) => {
        console.warn('Unhandled auth event:', event, ', data:', data)
      },

      onError: (error, event, data) => {
        console.error(`Auth listener error for event ${event}:`, error)
        if (onError) {
          onError(error, event, data)
        }
      }
    }),
    [dispatch, posthog, navigate, onError]
  )

  // Merge custom handlers with defaults, memoized to prevent re-renders
  const handlers = useMemo(
    () => ({
      ...defaultHandlers,
      ...customHandlers
    }),
    [defaultHandlers, customHandlers]
  )

  useEffect(() => {
    if (isSignedIn && userId && userEmail) {
      handleAuthEventSafely(
        AUTH_EVENT_TYPES.SIGN_IN,
        {
          userId,
          userEmail,
          firstName: user?.firstName,
          lastName: user?.lastName,
          imageUrl: user?.imageUrl,
          createdAt: user?.createdAt
        },
        handlers,
        eventFilter
      )
    }
  }, [isSignedIn, userId, userEmail, user, handlers, eventFilter])
}

export default useAuthListener
